import { useCallback, useEffect, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  Api,
  apiDataFetchingAtom,
  companyIdSelector,
  coursesCalendarRideStatusFilterAtom,
  coursesCalendarSlotsAtom,
  getCoursesCalendarCreateRidePath,
  getCoursesCalendarSlotsPath,
  ICoursesCalendarCreateRideResponse,
  ICoursesCalendarSlotDto,
  ICoursesCalendarSlotsResponse,
  SimpleResult,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

const entity = "course" as const;

export const useCoursesCalendarSlots = (): {
  slots: ICoursesCalendarSlotDto[];
  refetch: () => Promise<void>;
  createSlotRide: (slot: ICoursesCalendarSlotDto) => Promise<number>;
} => {
  const companyId = useRecoilValue(companyIdSelector);
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const rideStatusFilter = useRecoilValue(coursesCalendarRideStatusFilterAtom);
  const setApiFetching = useSetRecoilState(apiDataFetchingAtom);
  const setSlotsForSelection = useSetRecoilState(coursesCalendarSlotsAtom);
  const [slots, setSlots] = useState<ICoursesCalendarSlotDto[]>([]);

  const refetch = useCallback(async (): Promise<void> => {
    if (!companyId || !calendarDates?.date1 || !calendarDates?.date2) {
      setSlots([]);
      return;
    }

    setApiFetching(true);
    try {
      const path = getCoursesCalendarSlotsPath(companyId);
      const res = await Api.post<SimpleResult<ICoursesCalendarSlotsResponse>>(
        path,
        {
          dateFrom: calendarDates.date1,
          dateTo: calendarDates.date2,
          rideStatusFilter,
        },
      );
      if (!res?.data?.success || !res.data.data) {
        setSlots([]);
        return;
      }
      setSlots(res.data.data.slots ?? []);
    } finally {
      setApiFetching(false);
    }
  }, [calendarDates, companyId, rideStatusFilter, setApiFetching]);

  const createSlotRide = useCallback(
    async (slot: ICoursesCalendarSlotDto): Promise<number> => {
      if (!companyId) {
        throw new Error("Missing company id");
      }
      setApiFetching(true);
      try {
        const path = getCoursesCalendarCreateRidePath(companyId);
        const res = await Api.post<
          SimpleResult<ICoursesCalendarCreateRideResponse>
        >(path, {
          courseId: slot.courseId,
          direction: slot.direction,
          shiftTime: slot.shiftTime,
        });
        if (!res?.data?.success || !res.data.data) {
          throw new Error("Create ride failed");
        }
        const rideId = res.data.data.rideId;
        if (typeof rideId !== "number") {
          throw new Error("Invalid create-ride response");
        }
        await refetch();
        return rideId;
      } finally {
        setApiFetching(false);
      }
    },
    [companyId, refetch, setApiFetching],
  );

  useEffect(() => {
    setSlotsForSelection(slots);
  }, [setSlotsForSelection, slots]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { slots, refetch, createSlotRide };
};
